<script setup lang="ts">
import { onMounted } from 'vue';
import { closeModal, updateShow } from '@/app/components/modal/modals';
import type { ModalProps } from '@/app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { useI18N } from '@dialpad/i18n';
import logger from '@/logger';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import { useCampaignSettingsStore, useCampaignStore } from '@/stores';
import { showCampaignSyncToast } from '@/campaign/utils/sync-toast';
import { showErrorToast } from '@/noto';
import { DATA_QA } from './disable-local-dnc.constants';

const log = logger.getChild('DisableLocalDnc');

const props = defineProps<ModalProps>();

onMounted(() => {
  if (!props.meta?.id) {
    log.debug('Missing id, closing modal');
    void closeModal();
    return;
  }

  log.debug('Mounted');
});

const campaignStore = useCampaignStore();

const campaign = campaignStore.watch(
  () => props.meta?.id ?? null,
  'DisableLocalDnc',
);

const campaignSettingsStore = useCampaignSettingsStore();

const campaignSettings = campaignSettingsStore.watch(
  () => campaign.value.item?.settingsId ?? null,
  'DisableLocalDnc',
);

const { $t } = useI18N();

const confirm = () => {
  if (!campaign.value.item?.settingsId) {
    log.debug('Missing id');
    return;
  }
  campaignSettingsStore.update(
    campaign.value.item.settingsId,
    {
      enableInternalDnc: false,
    },
    {
      onSuccess: () => {
        void showCampaignSyncToast(true);
        void closeModal();
      },
      onError: (error) => {
        log.log('Error disabling local dnc', error);
        void showErrorToast({
          content: {
            body: {
              key: 'DISABLE_LOCAL_DNC_MODAL_ERROR',
            },
          },
        });
      },
      onRetry: () => {
        void showCampaignSyncToast(false);
      },
    },
  );
};

const cancel = () => {
  void closeModal();
};
</script>

<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('DISABLE_LOCAL_DNC_MODAL_TITLE')"
    :show="!!campaignSettings.item"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :data-loading="!campaignSettings.item"
    @update:show="updateShow"
  >
    <p>{{ $t('DISABLE_LOCAL_DNC_MODAL_CONFIRMATION') }}</p>
    <template #footer>
      <DtButton
        :data-qa="DATA_QA.CONFIRM_BUTTON"
        :loading="campaignSettings.loading"
        kind="danger"
        importance="primary"
        class="d-ml6"
        @click="confirm"
      >
        {{ $t('DISABLE_LOCAL_DNC_MODAL_CONFIRM') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CANCEL_BUTTON"
        importance="clear"
        @click="cancel"
      >
        {{ $t('DISABLE_LOCAL_DNC_MODAL_CANCEL') }}
      </DtButton>
    </template>
  </DtModal>
</template>
